import { SelectHTMLAttributes, forwardRef } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/cn";
import { Loader } from "@/components/ui/Loader";

export interface SelectOption {
  value: string;
  label: string;
  group?: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  loading?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      hint,
      options,
      placeholder = "Selecione...",
      loading = false,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

    /* Agrupa opções por `group` se houver */
    const grouped = options.reduce<Record<string, SelectOption[]>>((acc, opt) => {
      const g = opt.group ?? "__ungrouped__";
      if (!acc[g]) acc[g] = [];
      acc[g].push(opt);
      return acc;
    }, {});

    const hasGroups = Object.keys(grouped).some((k) => k !== "__ungrouped__");

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={selectId}
            className="text-xs font-semibold text-slate-500 uppercase tracking-wide"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={loading || props.disabled}
            className={cn(
              "w-full h-10 rounded-lg border bg-slate-50 pl-3 pr-9 text-sm text-slate-800 appearance-none",
              "placeholder:text-slate-400 transition-colors cursor-pointer",
              "focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-0 focus:border-brand-600",
              error
                ? "border-red-400 focus:ring-red-400"
                : "border-slate-200 hover:border-slate-300",
              (loading || props.disabled) && "opacity-60 cursor-not-allowed",
              className
            )}
            {...props}
          >
            <option value="">
              {loading ? "Carregando..." : placeholder}
            </option>

            {hasGroups
              ? Object.entries(grouped).map(([group, opts]) =>
                  group === "__ungrouped__" ? (
                    opts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))
                  ) : (
                    <optgroup key={group} label={group}>
                      {opts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  )
                )
              : options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
          </select>

          {loading ? (
            <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
              <Loader size="xs" color="slate" />
            </div>
          ) : (
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          )}
        </div>

        {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
        {hint && !error && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
    );
  }
);

Select.displayName = "Select";
